import './header';
import './item-action';
import './item-filter';
import './loop-filters';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-loop' )
export class AutomatorTemplateItemLoop extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The logic for evaluating loop filters.
	 * Common values: `intersection`, `union`.
	 *
	 * @type {String}
	 * @property {String} logic - Logic of the loop filters.
	 * Attribute name in HTML: `logic`.
	 * Default: `intersection`.
	 */
	@property( { type: String } )
	accessor logic = 'intersection';

	/**
	 * An array of loop filters defining the filtering logic for the loop block.
	 * Each filter contains relevant properties such as conditions and logic.
	 *
	 * @type {Array<Object>}
	 * @property {Array<Object>} loopFilters - The loop filters used in this block.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Array, attribute: false } )
	accessor loopFilters;

	/**
	 * Information about the data to be iterated, such as users, posts, or tokens.
	 *
	 * @type {Object}
	 * @property {Object} iterableExpression - Contains details about the data to be iterated.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Object, attribute: false } )
	accessor iterableExpression;

	/**
	 * The list of items (actions or filters) inside the loop block.
	 *
	 * @type {Array<Object>}
	 * @property {Array<Object>} items - Items to be rendered within the loop.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Array, attribute: false } )
	accessor items;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="loop">
				${ this._templateSummary }
				${ this._templateItems }
			</div>
		`;
	}

	/**
	 * Shows a summary of the loop, including the data that has to be iterated
	 *
	 * @return {TemplateResult} Summary of the loop block
	 */
	get _templateSummary() {
		return html`
			<div class="loop-summary">
				<uap-template-loop-header
					.iterableExpression=${ this.iterableExpression }
				></uap-template-loop-header>

				<uap-template-loop-filters
					.loopFilters=${ this.loopFilters }
				></uap-template-loop-filters>
			</div>
		`;
	}

	/**
	 * Returns the template result that contains all the items of the loop
	 *
	 * @return {TemplateResult} The items
	 */
	get _templateItems() {
		return html`
			<div class="loop-items">
				${ repeat(
					this.items,
					( item, index ) => index,
					item => {
						if ( item.type === 'action' ) {
							return html`
								<uap-template-loop-action 
									integration-code=${ item.integration_code }
									.title=${ item.title }
									.titleHTML=${ item.title_html }
									.delay=${ item.delay }

									?is-pro=${ item.is_pro }
									?is-elite=${ item.is_elite }
								></uap-template-loop-action>
							`;
						}

						if ( item.type === 'filter' ) {
							return html`
								<uap-template-loop-action-filter
									logic="${ item.logic }"
									.conditions=${ item.conditions }
									.items=${ item.items }
								></uap-template-loop-action-filter>
							`;
						}
					}
				) }
			</div>
		`;
	}
}